import { __assign, __read, __spread } from "tslib";
import { useMemo } from 'react';
import { combineProps, logInDev, omit, useDepsChange, useEventCallback, useRefState, } from '../../../foundation';
export var useDownshiftGroup = function (_a) {
    var groupBy = _a.groupBy, options = _a.options, filteredResult = _a.filteredResult, getExpandIconProps = _a.getExpandIconProps, groupExpanded = _a.groupExpanded, groupDefaultExpanded = _a.groupDefaultExpanded, onGroupExpanded = _a.onGroupExpanded, groupVariant = _a.groupVariant, getOptionDisabled = _a.getOptionDisabled, id = _a.id;
    var _b = __read(useRefState({}), 2), groupExpandedMapRef = _b[0], setGroupExpandedMap = _b[1];
    var isTitleGroup = groupVariant === 'normal';
    var handleGroupExpandedChange = function (groupName) {
        var _a;
        var groupExpandedMap = groupExpandedMapRef.current;
        if (typeof groupExpanded === 'boolean') {
            return;
        }
        var toExpandedState = !groupExpandedMap[groupName];
        var newExpandedMap = __assign(__assign({}, groupExpandedMap), (_a = {}, _a[groupName] = toExpandedState, _a));
        setGroupExpandedMap(newExpandedMap);
        if (onGroupExpanded) {
            var group = optionsGroupList.find(function (x) { return x.group === groupName; });
            if (group) {
                onGroupExpanded(__assign(__assign({}, omit(group, ['getExpandIconProps'])), { expanded: toExpandedState }), newExpandedMap);
            }
        }
    };
    var handleExpandIconClick = useEventCallback(function (e, groupName) {
        handleGroupExpandedChange(groupName);
        e.preventDefault();
        e.stopPropagation();
    });
    var optionsGroupList = useMemo(function () {
        /** key with this group count */
        if (groupBy && options) {
            // used to keep track of key and indexes in the result array
            var indexBy_1 = new Map();
            var warn_1 = false;
            var afterGroupFn_1 = [];
            var result = filteredResult.reduce(function (acc, option, index) {
                var group = groupBy(option);
                if (acc.length > 0 && acc[acc.length - 1].group === group) {
                    var currGroup = acc[acc.length - 1];
                    acc[acc.length - 1].options.push(__assign(__assign({}, option), { group: currGroup, indexInOwnGroup: currGroup.options.length - 1 }));
                }
                else {
                    if (process.env.NODE_ENV !== 'production') {
                        if (indexBy_1.get(group) && !warn_1) {
                            logInDev({
                                component: 'useDownshift',
                                comment: "Material-UI: The options provided combined with the `groupBy` method returns duplicated headers.\n                  You can solve the issue by sorting the options with the output of `groupBy`",
                            });
                            warn_1 = true;
                        }
                        indexBy_1.set(group, true);
                    }
                    var newGroup_1 = {
                        key: index,
                        index: index,
                        group: group,
                        order: acc.length,
                        options: [__assign(__assign({}, option), { indexInOwnGroup: 0 })],
                        expanded: false,
                        getExpandIconProps: function (additionExpandIconProps) {
                            return newGroup_1.options.length > 1
                                ? combineProps({
                                    onClick: function (e) {
                                        handleExpandIconClick(e, group);
                                    },
                                    onMouseDown: function (e) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    },
                                    tabIndex: -1,
                                }, combineProps(additionExpandIconProps, getExpandIconProps === null || getExpandIconProps === void 0 ? void 0 : getExpandIconProps(newGroup_1)))
                                : undefined;
                        },
                    };
                    newGroup_1.options[0].group = newGroup_1;
                    if (isTitleGroup) {
                        var groupTitleOption_1 = {
                            id: id + "-" + group,
                            label: group,
                            disabled: true,
                            // * here cause by self Type reference
                            group: newGroup_1,
                        };
                        // push fn to array, do that after all options be add into group
                        if (getOptionDisabled) {
                            afterGroupFn_1.push(function () {
                                groupTitleOption_1.disabled =
                                    getOptionDisabled(groupTitleOption_1);
                            });
                        }
                        newGroup_1.options.unshift(groupTitleOption_1);
                    }
                    acc.push(newGroup_1);
                }
                return acc;
            }, []);
            afterGroupFn_1.forEach(function (fn) {
                fn();
            });
            return result;
        }
        return [];
    }, [
        filteredResult,
        getExpandIconProps,
        getOptionDisabled,
        groupBy,
        handleExpandIconClick,
        id,
        isTitleGroup,
        options,
    ]);
    var initGroupExpandedState = function (expandedState) {
        if (typeof expandedState === 'object') {
            setGroupExpandedMap(expandedState, false);
        }
        else if (typeof expandedState === 'boolean') {
            setGroupExpandedMap(optionsGroupList.reduce(function (prev, curr) {
                prev[curr.group] = expandedState;
                return prev;
            }, {}), false);
        }
    };
    useDepsChange(function () {
        initGroupExpandedState(groupDefaultExpanded);
    }, []);
    useDepsChange(function () {
        initGroupExpandedState(groupExpanded);
    }, [groupExpanded]);
    var groupExpandedMap = groupExpandedMapRef.current;
    // * speared logic with group, prevent calculate every time.
    var groupedResult = useMemo(function () {
        var addExpandedResult = Object.entries(groupExpandedMap).reduce(function (prev, _a) {
            var _b = __read(_a, 2), key = _b[0], expended = _b[1];
            var group = prev.find(function (x) { return x.group === key; });
            if (group)
                group.expanded = expended;
            return prev;
        }, __spread(optionsGroupList));
        return addExpandedResult.reduce(function (prev, curr) {
            if (curr.expanded) {
                prev.push.apply(prev, __spread(curr.options));
            }
            else {
                prev.push(curr.options[0]);
            }
            return prev;
        }, []);
    }, [groupExpandedMap, optionsGroupList]);
    return {
        optionsGroupList: optionsGroupList,
        groupedResult: groupedResult,
        groupExpandedMap: groupExpandedMap,
        handleGroupExpandedChange: handleGroupExpandedChange,
    };
};
